// Copyright (c) 2026 Or2 LLC. All rights reserved.
// Insight Platform - proprietary software by Nnaico (https://nnaico.com).
// See the LICENSE file in this repository. All rights reserved.

import { createTheme } from '@mui/material/styles';
import { getSkin, resolvePalette } from './lib/appearance';

// The MUI theme = palette (scheme × mode, from public/themes.js) + style.
// No colors live here: the palette tokens are documented in themes.js.
//
// background.default = the conversation area; background.nav = the frame
// around it (sidebar, input bar); the header stays on `paper`. `nav` is
// exposed as --app-nav-bg for the plain CSS of the input bar.
// background.userBubble = the user's own messages.
const baseComponents = {
  MuiCssBaseline: {
    styleOverrides: (theme) => ({
      ':root': { '--app-nav-bg': theme.palette.background.nav },
    }),
  },
  // Popups (menus, popovers) sit on `paper`, often the same color as the
  // sidebar they open from; on dark surfaces the shadow is invisible, so a
  // divider-colored border keeps them distinct
  MuiPopover: {
    styleOverrides: {
      paper: ({ theme }) => (theme.palette.mode === 'dark'
        ? { border: `1px solid ${theme.palette.divider}` } : {}),
    },
  },
};

const muiPalette = (mode, p) => ({
  mode,
  primary: { main: p.accent },
  ...(p.info && { info: { main: p.info } }),
  background: {
    default: p.chatBg,
    paper: p.paper,
    nav: p.navBg,
    userBubble: p.userBubbleBg,
    userBubbleText: p.userBubbleText,
    // the bubble border is a terminal ornament, unless the scheme wants it always
    ...(p.userBubbleBorderAlways && { userBubbleBorder: p.userBubbleBorder }),
    chip: p.chipBg || p.selectedBg,
  },
  ...((p.textPrimary || p.textSecondary) && {
    text: {
      ...(p.textPrimary && { primary: p.textPrimary }),
      ...(p.textSecondary && { secondary: p.textSecondary }),
    },
  }),
  action: { hover: p.hoverBg, selected: p.selectedBg },
  ...(p.divider && { divider: p.divider }),
});

// --- Terminal style ---------------------------------------------------------
// Typography, square corners, accent-tinted borders. Colors still come from
// the palette, so terminal and normal share the same scheme.

const MONO_FONT =
  "'JetBrains Mono', 'Fira Code', 'Cascadia Mono', 'SF Mono', Consolas, Menlo, 'Courier New', monospace";

const monoVariants = ['h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'subtitle1', 'subtitle2',
  'body1', 'body2', 'caption', 'overline'];

const terminalStyle = (p) => ({
  palette: {
    divider: p.divider || p.tint(p.accent, 0.25),
    background: { userBubbleBorder: p.userBubbleBorder },
  },
  typography: {
    fontFamily: MONO_FONT,
    ...Object.fromEntries(monoVariants.map((v) => [v, { fontFamily: MONO_FONT }])),
    button: { fontFamily: MONO_FONT, letterSpacing: '0.04em' },
  },
  shape: { borderRadius: 0 },
  components: {
    MuiTooltip: {
      styleOverrides: { tooltip: { fontFamily: MONO_FONT, borderRadius: 0 } },
    },
    MuiPaper: {
      styleOverrides: { root: { backgroundImage: 'none' } },
    },
    // Terminal buttons are outlined, never filled: big solid accent
    // rectangles are what makes the style glare.
    MuiButton: {
      styleOverrides: {
        containedPrimary: {
          backgroundColor: p.tint(p.accent, 0.12),
          color: p.accentText,
          border: `1px solid ${p.tint(p.accent, 0.45)}`,
          boxShadow: 'none',
          '&:hover': { backgroundColor: p.tint(p.accent, 0.2), boxShadow: 'none' },
        },
      },
    },
  },
});

const cache = new Map();

export const buildTheme = (mode, style, scheme) => {
  const key = `${mode}|${style}|${scheme}`;
  if (!cache.has(key)) {
    const p = resolvePalette(scheme, mode);
    const base = createTheme({ palette: muiPalette(mode, p), components: baseComponents });
    cache.set(key, style === 'terminal' ? createTheme(base, terminalStyle(p)) : base);
  }
  return cache.get(key);
};

// Logged-out pages (login, invite, reset): default light scheme, normal style
// (an unknown/missing id resolves to the skin's first scheme)
export const lightTheme = buildTheme('light', 'normal', getSkin().defaults?.normal?.light);
